import classNames from 'classnames';
import type { ReactElement, RefObject } from 'react';
import { useCallback, useRef, useState } from 'react';
import type { VideoModalData, VideoModalProps } from './VideoModal.data';
import styles from './VideoModal.module.scss';
import Video from '../../atoms/video/Video';
import type { PlayerType, VideoBlokData, VideoRef } from '../../atoms/video/Video.data';
import { getPlayerType } from '../../atoms/video/utils';
import { observer } from 'mobx-react-lite';
import { useGlobalStore } from '../../../stores/global';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import Modal from '../modal/Modal';
import type { ModalRef } from '../modal/Modal.data';

function VideoModal({ className, onOpen, onClose, ref }: VideoModalProps): ReactElement {
  const store = useGlobalStore();
  const { makeVideoModalVisible, videoModalData } = store;

  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<ModalRef | null>;
  const vimeoPlayerRef = useRef<VideoRef>(null);
  const filePlayerRef = useRef<VideoRef>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [fileVideo, setFileVideo] = useState<VideoBlokData | undefined>();
  const [vimeoVideo, setVimeoVideo] = useState<VideoBlokData | undefined>();
  const [playerType, setPlayerType] = useState<PlayerType>();
  const onCloseCallback = useRef<() => void>(undefined);
  const [showModal, setShowModal] = useState(false);

  const getActivePlayer = useCallback(() => {
    if (playerType === 'vimeo') {
      return vimeoPlayerRef;
    }
    return filePlayerRef;
  }, [playerType, vimeoPlayerRef, filePlayerRef]);

  const hide = useCallback(() => {
    if (onClose) onClose();
    setIsPlaying(false);
    setShowModal(false);
  }, [onClose]);

  const show = useCallback(async (data: VideoModalData) => {
    // isContained:boolean
    setShowModal(true);
    onCloseCallback.current = data?.options?.onClose;
    const playerType = getPlayerType(data?.video);
    setPlayerType(playerType);
    if (playerType === 'vimeo') {
      setVimeoVideo(data?.video);
    } else if (playerType === 'file') {
      setFileVideo(data?.video);
    }
  }, []);

  useUpdateEffect(() => {
    if (makeVideoModalVisible) {
      show(videoModalData);
    } else {
      hide();
    }
  }, [makeVideoModalVisible]);

  const handleCloseStart = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const handleClose = useCallback(() => {
    store.closeVideoModal();
    setShowModal(false);
    setIsPlaying(false);
    getActivePlayer().current?.playerRef.current?.seekTo(0);
    setPlayerType(undefined);
    if (onCloseCallback.current) {
      onCloseCallback.current();
    }
  }, [getActivePlayer, store]);

  const handleShow = useCallback(() => {
    if (onOpen) onOpen();
    setIsPlaying(true);
  }, [onOpen]);

  return (
    <Modal
      ref={elementRef}
      className={classNames(styles.videoModal, 'abs-fill', className)}
      backgroundClassName={styles.videoModalBackground}
      onClose={handleClose}
      onCloseStart={handleCloseStart}
      onShow={handleShow}
      open={showModal}
      title={videoModalData?.title}
    >
      {showModal && (
        <>
          {vimeoVideo && (
            <Video
              ref={vimeoPlayerRef}
              playerType="vimeo"
              blok={vimeoVideo}
              objectFit="contain"
              controls
              openInModal={false}
              playing={isPlaying && playerType === 'vimeo'}
              playButton
              muted={false}
              removeOnPause={false}
              excludePoster
              enableFullscreenButton={false}
              requireClickListener
              lazyLoad={false}
              className={classNames(styles.video, {
                [styles.isVisible]: playerType === 'vimeo',
              })}
            />
          )}

          {fileVideo && (
            <Video
              ref={filePlayerRef}
              blok={fileVideo}
              playerType="file"
              objectFit="contain"
              controls
              openInModal={false}
              playing={isPlaying && playerType === 'file'}
              playButton
              muted={false}
              excludePoster
              removeOnPause={false}
              enableFullscreenButton={false}
              lazyLoad={false}
              requireClickListener
              className={classNames(styles.video, {
                [styles.isVisible]: playerType === 'file',
              })}
            />
          )}
        </>
      )}
    </Modal>
  );
}

export default observer(VideoModal);
